import { Component, OnInit, signal } from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { AccountSignatureModel } from "src/app/shared/models/account-signature.model";
import { AppSpinnerModule } from "src/app/shared/modules/app-spinner.module";
import { AccountSignatureService } from "src/app/shared/services/account-signature.service";
import { AppDialogModule } from "../../shared/components/app-dialog/app-dialog.component";
import { AddAccountSignatureComponent } from "../add-account-signature/add-account-signature.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ActivatedRoute } from "@angular/router";

import type { ImageTypeResult } from "image-type";

@Component({
    selector: "app-account-signature",
    templateUrl: "./account-signature.component.html",
    styleUrls: ["./account-signature.component.scss"],
    standalone: false
})
export class AccountSignatureComponent implements OnInit {
  signatures = signal<AccountSignatureModel[]>([]);
  signatureImage?: SafeUrl;
  isReadDataFromSassEnabled: boolean = false;

  constructor(
    private readonly accountSignatureService: AccountSignatureService,
    private readonly sanitizer: DomSanitizer,
    private readonly toastr: ToastrService,
    private readonly dialog: AppDialogModule,
    private readonly spinner: AppSpinnerModule,
    private readonly route: ActivatedRoute,
    private readonly modalService: NgbModal
  ) { }

  ngOnInit() {
    this.isReadDataFromSassEnabled = this.route.snapshot.data.isReadDataFromSassEnabled;
    this.getSignaturesForCurrentUser();
  }

  async getSignaturesForCurrentUser(): Promise<void> {
    this.spinner.show();

    const { default: lazyImageType } = await import("image-type");

    try {
      const { accountSignatures } = await firstValueFrom(
        this.accountSignatureService.getCurrentAccountSignatures(
          this.isReadDataFromSassEnabled
        )
      );

      this.signatures.set(await this.getSignatureMetadata(accountSignatures, lazyImageType));
    } catch (err) {
      this.toastr.error("Failed to load account signatures");
      console.info("getSignaturesForCurrentUser error", err);
      this.signatures.set([]);
    } finally {
      this.spinner.hide();
    }
  }

  async getSignatureMetadata(
    accountSignatures: AccountSignatureModel[],
    lazyImageType: (
      input: ArrayBuffer | Uint8Array
    ) => Promise<ImageTypeResult | undefined>
  ): Promise<AccountSignatureModel[]> {
    return Promise.all(
      accountSignatures.map(async (signature) => ({
        ...signature,
        imageType: await lazyImageType(
          this.getUint8ArrayFromBase64(signature.signatureImageBase64)
        ),
      }))
    );
  }

  deleteSignatureById(accountSignature: AccountSignatureModel) {
    this.spinner.show();
    this.accountSignatureService.deleteSignature(this.isReadDataFromSassEnabled, accountSignature).subscribe(
      () => {
        this.spinner.hide();
        this.toastr.success("Signature deleted successfully");
        this.getSignaturesForCurrentUser();
      },
      (err) => {
        this.spinner.hide();
        this.toastr.error(err.error.error);
      }
    );
  }

  deleteConfirmationDialog(accountSignature: AccountSignatureModel) {
    this.dialog.show({
      title: "Delete Signature",
      message: "Are you sure you want to delete this signature?",
      noButtonText: "Cancel",
      yesButtonText: "Delete Signature",
    }).result.then((dialogResult) => {
      if (dialogResult === "yes") {
        this.deleteSignatureById(accountSignature);
      }
    });
  }

  getSanitizedImage(image: string, imageType?: ImageTypeResult): SafeUrl {
    const mime = imageType?.mime || "image/png";

    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `data:${mime};base64,${image}`
    );
  }

  getUint8ArrayFromBase64(base64: string): Uint8Array {
    return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  }

  openAddAccountSignatureModal() {
    const modalRef = this.modalService.open(AddAccountSignatureComponent, {
      size: "full-screen",
      backdrop: "static",
      keyboard: false,
    });

    modalRef.componentInstance.isReadDataFromSassEnabled = this.isReadDataFromSassEnabled;

     modalRef.result.then((result) => {
      if (result === "accountSignatureAdded") {
        this.onAccountSignatureAdded();
      }
    });
  }

  onAccountSignatureAdded(): void {
    this.getSignaturesForCurrentUser();
  }
}
